import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useContext, useState } from "react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { isValidPrefix } from "@/constants";
import { transactionService } from "@/services/transactionService";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { AuthContext } from "@/contexts/authContext";
import { useNavigate } from "react-router-dom";

const formSchema = z.object({
    amount: z.string()
        .min(1, "Le montant est requis")
        .regex(/^\d+$/, "Le montant ne doit contenir que des chiffres")
        .refine((val) => parseInt(val, 10) >= 500, "Le montant doit être supérieur ou égal à 500 fr"),
    displayName: z.string().min(1, "Veuillez entrer un nom"),
    phoneNumber: z
        .string()
        .min(8, "Le numéro de téléphone doit contenir 8 chiffres")
        .refine((phone) => isValidPrefix(phone), {
            message: "Le numéro de téléphone doit commencer par un préfixe valide",
        }),
});



export type FormValues = z.infer<typeof formSchema>;

export function WithdrawForm() {
    const { user } = useContext(AuthContext);
    const navigation = useNavigate();

    const [isloading, setIsLoading] = useState(false);
    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            amount: "500",
            displayName: "",
            phoneNumber: "",
        },
    });

    const onSubmit = async (data: FormValues) => {
        if(isloading){
            return;
        }
        setIsLoading(true);
        
        if (user != null) {
            try {
                const withdrawPromise = await transactionService.withdrawMoney(
                    user?.id,
                    parseInt(data.amount, 10),
                    `226${data.phoneNumber}`,
                    data.displayName,
                );
                console.log("withdrawPromise", withdrawPromise);

                if (!withdrawPromise.success) {
                    throw new Error( (withdrawPromise as any).message || (withdrawPromise as any).error || "Une erreur s'est produite");
                }
                navigation("/dashboard")
                toast.promise(Promise.resolve(withdrawPromise), {
                    loading: "Traitement en cours...",
                    success: !withdrawPromise.error ? "Retrait effectué avec succès." : withdrawPromise.error.message,
                    error: (withdrawPromise as any).message || (withdrawPromise as any).error || "Une erreur s'est produite",
                });


            } catch (error: any) {
                toast.error(error.message || "Une erreur s'est produite");

                if (error instanceof Error) {
                    console.error("Erreur :", error.message);
                } else {
                    console.error("Erreur inconnue :", error);
                }
            } finally {
                setIsLoading(false);
            }
        }
    };



    return (

        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <Card className="w-full max-w-3xl mt-5">
                    <CardHeader>
                        <CardTitle>Retrait de commissions</CardTitle>
                        <CardDescription>Remplissez le formulaire ci-dessous pour proceder au retrait de vos commisions.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div>
                            <div className="text-lg font-medium">Solde actuel</div>
                            <div className="text-2xl font-bold">{user?.balance ? new Intl.NumberFormat("fr-FR", {
                                style: "currency",
                                currency: "XOF",
                            }).format(user?.balance) : ""}</div>
                        </div>
                        <div className="flex flex-col gap-6">
                            <div className="grid gap-2">
                                <FormLabel htmlFor="phoneNumber">Numéro de téléphone</FormLabel>
                                <FormField
                                    control={form.control}
                                    name="phoneNumber"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <Input id="phoneNumber" type="tel" placeholder="Votre numéro de téléphone" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <div className="grid gap-2">
                                <FormLabel htmlFor="amount">Montant du retrait</FormLabel>
                                <FormField
                                    control={form.control}
                                    name="amount"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <Input id="amount" type="number" placeholder="Montant du retrait" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <div className="grid gap-2">
                                <FormLabel htmlFor="displayName">Nom du compte mobile money</FormLabel>
                                <FormField
                                    control={form.control}
                                    name="displayName"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <Input id="displayName" type="text" placeholder="Nom du compte" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>
                    </CardContent>
                    <CardFooter className="flex items-center">
                        <Button variant="default" type="submit" disabled={isloading}>
                            {isloading ? <Loader2 className="animate-spin" /> : "Soumettre"}
                        </Button>
                    </CardFooter>
                </Card>

            </form>
        </Form>

    );
}
